import image_logo from "../../assets/images/logo.svg";
import { PROVIDER, controllerLine, providerRoleSentence } from "../../config/legalEntities";

function PrivacyPolicySubscriber() {
    const appName = process.env.REACT_APP_NAME || "TruHail";
    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 py-10">
            <div className="mb-8 h-[80px] overflow-hidden rounded-2xl">
                <img
                    src={image_logo}
                    alt={`Logo ${appName}`}
                    height={80}
                    className="h-full w-full object-cover"
                />
            </div>

            <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-md">
                <h1 className="mb-4 text-2xl font-bold">
                    Política de Privacidade — Assinante (Operadora)
                </h1>
                <p className="mb-6 text-sm text-gray-500">
                    Última atualização: 26 de fevereiro de 2026
                </p>
                <p className="mb-8 text-lg">
                    Esta Política de Privacidade descreve como a Provedora da Plataforma, {PROVIDER.name} (CNPJ {PROVIDER.cnpj}), coleta, utiliza e protege os dados da pessoa física ou jurídica que licencia a Plataforma {appName} (doravante "Assinante" ou "Operadora").
                </p>

                <div className="space-y-8">
                    {/* 1. Diferenciação Controladora/Operadora */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">1. Papéis no Tratamento de Dados (LGPD)</h2>
                        <ul className="list-inside list-disc space-y-1">
                            <li>
                                <strong>Assinante como Titular:</strong> Para os dados de contato, financeiros e de acesso do próprio Assinante, a Provedora atua como <strong>Controladora</strong>.
                            </li>
                            <li>
                                <strong>Assinante como Controladora:</strong> Para os dados dos Usuários Finais (Motoristas e Passageiros) cadastrados pelo Assinante, o Assinante — {controllerLine} — atua como <strong>Controladora</strong>. A {PROVIDER.name} nunca é Controladora destes dados.
                            </li>
                        </ul>
                        <p className="mt-2 text-sm text-gray-600">{providerRoleSentence}</p>
                    </div>

                    {/* 2. Dados Coletados */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">2. Dados Coletados do Assinante</h2>
                        <ul className="list-inside list-disc space-y-1">
                            <li><strong>Dados de Identificação:</strong> Razão Social, CNPJ, Nome do Representante Legal, CPF, endereço da sede.</li>
                            <li><strong>Dados de Contato:</strong> E-mail corporativo, telefones de contato.</li>
                            <li><strong>Dados Financeiros:</strong> Chaves PIX, dados bancários para repasse (quando aplicável), chaves de API de gateways de pagamento (ex: Mercado Pago).</li>
                            <li><strong>Dados Locais:</strong> Logs de acesso ao Painel (IP, data, hora, agente de usuário).</li>
                        </ul>
                    </div>

                    {/* 3. Finalidade e Base Legal */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">3. Finalidade do Tratamento</h2>
                        <ul className="list-inside list-disc space-y-1">
                            <li><strong>Execução de Contrato (Art. 7º V LGPD):</strong> Concessão de acesso ao Painel de Controle, prestação de suporte técnico, processamento do licenciamento (SaaS).</li>
                            <li><strong>Cumprimento de Obrigação Legal (Art. 7º II LGPD):</strong> Emissão de notas fiscais, guarda de logs de acesso por 6 meses (Marco Civil da Internet).</li>
                            <li><strong>Legítimo Interesse (Art. 7º IX LGPD):</strong> Melhoria da usabilidade do Painel, comunicações sobre novas funcionalidades.</li>
                        </ul>
                    </div>

                    {/* 4. Compartilhamento Restrito */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">4. Compartilhamento de Dados</h2>
                        <p className="mb-2">Os dados cadastrais e de uso do Assinante não são vendidos. Contudo, para a operação da Plataforma, há compartilhamento estritamente necessário com subprocessadores (operadores) contratados, limitado às finalidades descritas. O compartilhamento ocorre apenas com:</p>
                        <ul className="list-inside list-disc space-y-1">
                            <li>Fornecedores de infraestrutura de hospedagem em nuvem (ex: Firebase/Google).</li>
                            <li>Processadores de pagamento para a contratação da licença SaaS e para o processamento de pagamentos dos Usuários Finais (ex: Mercado Pago).</li>
                            <li>Provedores de comunicação (notificações push, SMS e e-mail) e de mapas/geocodificação, conforme listados na seção 5.</li>
                            <li>Autoridades judiciais ou administrativas mediante ordem legal válida.</li>
                        </ul>
                        <p className="mt-2 text-sm text-gray-600">A lista completa de provedores e subprocessadores, com as respectivas finalidades e políticas de privacidade, encontra-se na seção 5 abaixo.</p>
                    </div>

                    {/* 5. Provedores e Subprocessadores */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">5. Provedores e Subprocessadores</h2>
                        <p className="mb-2">A Plataforma opera com o apoio dos seguintes provedores e subprocessadores. O compartilhamento de dados com cada um limita-se ao estritamente necessário à finalidade indicada:</p>
                        <ul className="list-inside list-disc space-y-1">
                            <li>
                                <strong>Mercado Pago</strong> — pagamentos —{" "}
                                <a href="https://www.mercadopago.com.br/privacidade" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>OneSignal</strong> — notificações push —{" "}
                                <a href="https://onesignal.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>SendPulse</strong> — SMS e e-mail —{" "}
                                <a href="https://sendpulse.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>OpenStreetMap</strong> — mapas/geocodificação —{" "}
                                <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>CARTO</strong> — mapas —{" "}
                                <a href="https://carto.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>Firebase/Google</strong> — hospedagem —{" "}
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>Apple App Store</strong> — distribuição de aplicativo —{" "}
                                <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>Google Play</strong> — distribuição de aplicativo —{" "}
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                            <li>
                                <strong>OpenAI</strong> — apenas análise agregada e anônima, sem dados pessoais (PII) —{" "}
                                <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">política de privacidade</a>
                            </li>
                        </ul>
                        <p className="mt-2 text-sm text-gray-600">As URLs e versões das políticas de terceiros podem mudar; confira na data de acesso. Para exercício de direitos ou dúvidas sobre estes subprocessadores, contate nosso DPO em{" "}
                            <a href="mailto:dpo@trusom.com" className="text-blue-600 underline">dpo@trusom.com</a>.
                        </p>
                    </div>

                    {/* 6. Segurança da Informação */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">6. Medidas de Segurança Tecnológicas</h2>
                        <p>O Painel Administrativo utiliza criptografia em trânsito (HTTPS), controle de acesso autenticado (tokens JWT), restrição de recursos baseada em escopo de locatário (Multi-tenant) e políticas rigorosas de senhas para proteger tanto a conta do Assinante quanto a sua base de Usuários Finais.</p>
                    </div>

                    {/* 7. Retenção e Exclusão */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">7. Retenção de Dados e Fim do Contrato</h2>
                        <ul className="list-inside list-disc space-y-1">
                            <li>Os dados do Assinante são retidos durante toda a vigência do contrato.</li>
                            <li>Após o cancelamento da licença, os dados comerciais da Operadora podem ser guardados por até 5 anos para fins contábeis e fiscais.</li>
                            <li>A base de dados de Usuários Finais pertencente ao Assinante pode ser exportada e, em seguida, eliminada dos servidores da Provedora conforme previsto no Contrato SaaS, ressalvadas as obrigações legais de retenção.</li>
                        </ul>
                    </div>

                    {/* 8. Contato do DPO */}
                    <div>
                        <h2 className="mb-3 text-xl font-semibold">8. Direitos e Contato (DPO)</h2>
                        <p>O Assinante pode exercer seus direitos (acesso, correção, portabilidade) ou sanar dúvidas de adequação contatando nosso DPO através do e-mail:</p>
                        <p className="mt-2 font-medium">
                            <a href="mailto:dpo@trusom.com" className="text-blue-600 underline">dpo@trusom.com</a>
                        </p>
                    </div>

                    {/* Links Relacionados */}
                    <div className="border-t border-gray-200 pt-6 mt-8">
                        <h3 className="mb-2 font-semibold">Documentos Relacionados</h3>
                        <ul className="list-inside list-disc space-y-1">
                            <li>
                                <a href="/terms-subscriber" className="text-blue-600 underline">
                                    Termos de Serviço — Assinante
                                </a>
                            </li>
                        </ul>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default PrivacyPolicySubscriber;
